import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getConnectionHealth } from "../services/socialConnectionsService";
import { getFreshIdToken } from "../auth/getFreshIdToken";
import { isBingConfigured } from "../features/bing/config/bingEnv";
import type {
  ConnectionAlert,
  SocialConnectionHealth,
} from "../types/socialConnections";

// Warn this far ahead of an unrecoverable expiry so the customer can reconnect
// before publishing breaks.
const SOFT_WARNING_WINDOW_SECONDS = 14 * 24 * 60 * 60;
const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Facebook / Instagram have no refresh token and no background refresh sweep, so
// the access token's own expiry (expires_at, ~60d) is the wall. Everywhere else
// — LinkedIn and TikTok — the sweep keeps the access token fresh, so only the
// refresh-token wall (refresh_expires_at, ~365d for LinkedIn) is unrecoverable.
const EXPIRES_AT_PLATFORMS = new Set(["FACEBOOK", "INSTAGRAM"]);

// Platforms whose reconnect the bell can actually drive (a ConnectPage OAuth
// handler exists — see ConnectionAlertBell.reconnectPlatformKey and the reconnect
// effect in ConnectPage; keep those and this set in sync). The health endpoint
// also returns GMB / YouTube / Search Console; those are dropped here rather than
// shown with a "Reconnect" button that goes nowhere. Bing's "reconnect" opens its
// API-key panel (SGL-134).
const RECONNECTABLE_PLATFORMS = new Set([
  "LINKEDIN", "FACEBOOK", "INSTAGRAM", "TIKTOK", "BING_WEBMASTER",
]);

// Connected with a key the owner pasted: no token expiry to warn about, only the
// owner's own reminder to replace the key (rotate_by, set by social-management).
const API_KEY_PLATFORMS = new Set(["BING_WEBMASTER"]);

function platformOf(c: SocialConnectionHealth): string {
  return (c.platform || "").toUpperCase();
}

function unrecoverableExpiry(c: SocialConnectionHealth): number | undefined {
  return EXPIRES_AT_PLATFORMS.has(platformOf(c))
    ? c.expires_at
    : c.refresh_expires_at;
}

/**
 * An API-key connection is red when its row is dead or is not a key at all (a
 * Bing row left from the OAuth build, which the backend now answers needs_reauth),
 * and amber from rotate_by on: exactly at it, not a warning window before it. A
 * key Bing has started refusing can't show here (the pull can't mark the row);
 * the dashboard and the agent say that one.
 */
function apiKeyAlert(c: SocialConnectionHealth): ConnectionAlert | null {
  const base = { platform: c.platform, profileId: c.profile_id, name: c.name };
  if ((c.status && c.status !== "active") || c.token_kind !== "api_key") {
    return { ...base, level: "red" };
  }
  if (c.rotate_by && Math.floor(Date.now() / 1000) >= c.rotate_by) {
    return { ...base, level: "amber", reason: "rotate_key" };
  }
  return null;
}

function toAlert(c: SocialConnectionHealth): ConnectionAlert | null {
  if (API_KEY_PLATFORMS.has(platformOf(c))) return apiKeyAlert(c);
  const base = { platform: c.platform, profileId: c.profile_id, name: c.name };
  if (c.status && c.status !== "active") {
    return { ...base, level: "red" };
  }
  const wall = unrecoverableExpiry(c);
  if (wall && wall - Math.floor(Date.now() / 1000) < SOFT_WARNING_WINDOW_SECONDS) {
    return { ...base, level: "amber" };
  }
  return null;
}

interface UseConnectionHealth {
  isAuthenticated: boolean;
  customerId: string | null | undefined;
}

/**
 * Polls the social-management connection-health endpoint for the selected
 * customer and reduces it to the alarm-bell view: which connections are dead
 * (red) or nearing an unrecoverable expiry (amber).
 *
 * Refreshes every 5 minutes and whenever the window regains focus. Silent on
 * failure — the bell simply shows nothing rather than an error.
 *
 * Mount ONE of these per app (see ConnectionHealthProvider) — every instance
 * runs its own poll.
 */
export function useConnectionHealth({
  isAuthenticated,
  customerId,
}: UseConnectionHealth): {
  alerts: ConnectionAlert[];
  redCount: number;
  amberCount: number;
  loading: boolean;
  refetch: () => void;
} {
  const [connections, setConnections] = useState<SocialConnectionHealth[]>([]);
  const [loading, setLoading] = useState(false);
  // Monotonic request id: a response is only applied if it is still the latest
  // request. Bumped on every load and on cleanup, so an in-flight fetch for a
  // previous customer can never overwrite the current one.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!isAuthenticated || !customerId) {
      setConnections([]);
      return;
    }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      const idToken = await getFreshIdToken();
      if (!idToken || myReq !== reqIdRef.current) return;
      const data = await getConnectionHealth(customerId, idToken);
      if (myReq === reqIdRef.current) setConnections(data);
    } catch {
      // getConnectionHealth never throws; a token-resolve failure just means the
      // bell shows nothing this cycle. Next poll retries.
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [isAuthenticated, customerId]);

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      setConnections([]);
      return;
    }

    load();
    const interval = window.setInterval(load, POLL_INTERVAL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);

    return () => {
      reqIdRef.current++; // invalidate any in-flight response
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuthenticated, customerId, load]);

  const alerts = useMemo(
    () =>
      connections
        .filter((c) => RECONNECTABLE_PLATFORMS.has(platformOf(c)))
        // No Bing card in this environment means nowhere to fix it from.
        .filter((c) => platformOf(c) !== "BING_WEBMASTER" || isBingConfigured())
        .map(toAlert)
        .filter((a): a is ConnectionAlert => a !== null),
    [connections]
  );
  const redCount = alerts.filter((a) => a.level === "red").length;
  const amberCount = alerts.length - redCount;

  return { alerts, redCount, amberCount, loading, refetch: load };
}
